/* Recorded interruptions, answered for someone who is not a network engineer:
 * what happened, where it broke, how we know and what to do. The measurements
 * sit behind one disclosure. A recorded start is not an outage duration. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiActivity, FiAlertTriangle, FiHelpCircle,
  FiChevronDown, FiChevronRight, FiChevronLeft, FiCopy, FiX, FiSearch, FiRefreshCw, FiPause, FiPlay, FiClock, FiList,
} from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { ago, fmtBytes } from "../utils/formatters";
import useVisibleInterval from "../hooks/useVisibleInterval";
import useEdgeFeature, { FEATURE_ABSENT } from "../hooks/useEdgeFeature";
import usePhoneLayout from "../hooks/usePhoneLayout";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import FlowPath, { PathStrip, delivered } from "./FlowPath";
import ContinuityTimeline, { timelineTimestamp } from "./ContinuityTimeline";
import { assessedSummary, assessedEpisode } from "./flowAssessment";
import { KINDS, headline, isPause, placeOf, reading } from "./flowWords";
import StallChart from "./StallChart";
import { useWanName, WanLabel } from "./WanNames";
import "./FlowContinuityPanel.css";

function when(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function clockOnly(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

const count = (value) => Number.isFinite(value) ? value.toLocaleString() : "Not measured";
const progress = (p) => p && Number.isFinite(p.pkts)
  ? `${p.pkts.toLocaleString()} packets${p.bytes ? ` · ${fmtBytes(Number(p.bytes) || 0)}` : ""}`
  : "not measured";
const circuitLoss = (circuit) => circuit?.observed === true && circuit?.wan && Number.isFinite(circuit.loss_ratio)
  ? `${(circuit.loss_ratio * 100).toFixed(1)}%` : "Not measured";
const confidenceLabel = (ep) => ep.needs_reassessment ? "Needs reassessment" : ep.segment === "inconclusive" ? "Inconclusive"
  : ep.segment === "past_anchor" ? "Unresolved"
    : ep.confidence === "confirmed" ? "Confirmed"
      : ep.confidence === "probable" ? "Probable" : "Confidence unavailable";
const flowKey = (f) => [f.key?.proto, f.key?.device, f.key?.device_port, f.key?.dst, f.key?.dst_port, f.key?.anchor_port].join("|");
const endpoint = (host, port) => `${host || "unknown"}${port != null ? `:${port}` : ""}`;
const recordedTime = (ep) => timelineTimestamp(ep.stalled_at) ?? NaN;
const windowReading = (win) => !win ? "Not measured: the anchor was not asked at both ends of the window"
  : !win.matched ? `Not comparable: ${win.note || "the two readings could not be matched"}`
    : `Same interval at both ends: sent ${count(win.edge_fwd?.pkts)}, anchor counted ${count(win.anchor_fwd?.pkts)} arriving; anchor counted ${count(win.anchor_rev?.pkts)} replies, this appliance ${count(win.edge_rev?.pkts)}`;
const anchorReading = (anchor) => !anchor ? "Anchor evidence unavailable"
  : !anchor.found ? "No record of this flow"
    : !anchor.counted ? "Tracked this flow, but packet counts were unavailable"
      : `Lifetime totals: ${count(anchor.forwarded_pkts)} forward packets; ${count(anchor.replied_pkts)} reply packets (not a stall-window measurement)`;

// Use the same evidence boundaries in a ticket as in the on-screen readings.
function asText(ep) {
  const ev = ep.evidence || {};
  const read = reading({ ...ep.verdict, recovered: ev.recovered, closing_state: ev.closing_state });
  const lines = [
    `Interruption on ${ep.node || "this appliance"}: ${read.where} (${read.sure})`,
    `Recorded verdict: ${ep.verdict?.segment || "unknown"} (${confidenceLabel(ep.verdict || {})})`,
    `Episode: ${ep.id || "unknown"}`,
    ep.verdict?.summary || "No summary available.",
    "",
    `Flow: ${ep.class || "unclassified"} ${endpoint(ep.key?.device, ep.key?.device_port)} -> ${endpoint(ep.key?.dst, ep.key?.dst_port)}`,
    `Stalled: ${ep.stalled_at || "unknown"} (sample interval: ${ep.sample_interval || "not provided"})`,
    `Observation window: ${ev.observed_from || "unknown"} to ${ev.observed_until || "unknown"}; ${ev.observation_complete ? "complete" : "completeness not established"}`,
    `Circuit: ${ev.circuit?.wan || "unknown"}${ev.circuit?.degraded ? " (degraded)" : ""}; probe loss: ${circuitLoss(ev.circuit)}`,
    `Sent after the stall: ${progress(ev.fwd_after)}`,
    `Received after it: ${progress(ev.rev_after)}`,
    `Anchor: ${anchorReading(ev.anchor)}`,
    `Matched window: ${windowReading(ev.anchor_window)}`,
    `Reachability: ${ev.reach?.attempted ? `${count(ev.reach.received)}/${count(ev.reach.sent)} probes answered` : ev.reach?.note || "not measured"}`,
    "", "Why:", ...(ep.verdict?.because || []).map((reason) => `  - ${reason}`),
  ];
  if (ep.verdict?.missing?.length) {
    lines.push("", "Not established:", ...ep.verdict.missing.map((item) => `  - ${item}`));
  }
  return lines.join("\n");
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`fc-stat${tone ? ` tone-${tone}` : ""}`}>
      <span className="fc-stat-label">{label}</span>
      <span className="fc-stat-value">{value}</span>
      {sub && <span className="fc-stat-sub">{sub}</span>}
    </div>
  );
}

function Fact({ label, mono, children }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className="fc-fact">
      <span className="fc-fact-label">{label}</span>
      <span className={`fc-fact-value${mono ? " mono" : ""}`}>{children}</span>
    </div>
  );
}

const TRAIL_MARKS = { good: "✓", bad: "✕", none: "?" };

/* The watched flows' columns. A phone shows each flow as a card, every value
   under its column's name (data-label). */
const FLOW_COLUMNS = ["Status", "Class", "Device", "Destination", "Movement", "Sent packets", "Reply packets"];

/* The measurements told as a story, in the order the connection travels. A tick
 * means traffic was seen moving, a cross means none was, and a question mark
 * means it was not measured, which is never shown as zero. */
function trail(ev, wanName, old) {
  const steps = [];
  const add = (tone, text, detail) => steps.push({ tone, text, detail });
  const sent = ev.fwd_after?.pkts;
  const back = ev.rev_after?.pkts;
  const win = ev.anchor_window;
  // Older records predate this counter, and a field never recorded reads back as zero.
  const other = old ? undefined : ev.device_other_sent_pkts;

  if (!Number.isFinite(sent)) add("none", "What the device sent after the freeze was not measured");
  else if (sent > 0) add("good", "The device kept sending", `${count(sent)} packets after the freeze`);
  else add("bad", "The device sent nothing more on this connection");

  // Whether the rest of the device was alive only matters once this connection went quiet.
  if (sent === 0 && other > 0) add("good", "The same device kept using the network for other things", `${count(other)} packets on its other connections`);
  else if (sent === 0 && other === 0) add("bad", "The device sent nothing on its other connections either");

  if (win?.matched) {
    if (win.edge_fwd?.pkts > 0) {
      add(delivered(win) ? "good" : "bad",
        delivered(win) ? "The Weavian cloud received what this box sent" : "Not everything this box sent reached the Weavian cloud",
        `${count(win.anchor_fwd?.pkts)} of ${count(win.edge_fwd.pkts)} packets, counted over the same seconds at both ends`);
    }
    if (win.anchor_rev?.pkts > 0) add("good", "The service answered, and its replies reached the Weavian cloud", `${count(win.anchor_rev.pkts)} replies in those same seconds`);
    else add("bad", "Nothing came back to the Weavian cloud from the service");
  } else if (win) add("none", "The two ends could not be compared over the same seconds");

  if (!Number.isFinite(back)) add("none", "Replies reaching this box after the freeze were not measured");
  else if (back > 0) add("good", "Replies were still reaching this box", `${count(back)} packets after the freeze`);
  else add("bad", win?.matched && win.anchor_rev?.pkts > 0 ? "But none of those replies reached this box" : "Nothing came back to this box");

  if (ev.retries?.flows > 0) {
    add(ev.retries.unreplied_flows > 0 ? "bad" : "good", `The device tried again ${count(ev.retries.flows)} ${ev.retries.flows === 1 ? "time" : "times"}`,
      ev.retries.unreplied_flows > 0 ? `${count(ev.retries.unreplied_flows)} got no answer` : "and was answered");
  }

  if (ev.circuit?.observed === true && ev.circuit.wan && Number.isFinite(ev.circuit.loss_ratio)) {
    add(ev.circuit.degraded ? "bad" : ev.circuit.loss_ratio === 0 ? "good" : "none",
      `The internet line (${wanName(ev.circuit.wan)}) lost ${circuitLoss(ev.circuit)} of its test packets`,
      ev.circuit.degraded ? "the box had marked this line as struggling" : "");
  } else add("none", "The internet line's health was not measured at that moment");

  if (ev.recovered) add("good", "The connection came back on its own while we were watching");
  if (ev.closing_state) add("none", "The connection was being closed at that moment", "that is how a call or session normally ends");
  if (ev.incomplete?.length > 0) add("none", "Our recording of this moment has gaps");
  return steps;
}

function EpisodeDetail({ episode }) {
  const wanName = useWanName();
  const [copyState, setCopyState] = useState("");
  const ev = episode.evidence || {};
  const read = reading({ ...episode.verdict, recovered: ev.recovered, closing_state: ev.closing_state });
  const evidenceText = asText(episode);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(evidenceText);
      setCopyState("copied");
    } catch {
      setCopyState("manual");
    }
  };

  return (
    <div className="fc-detail">
      <ol className="fc-story">
        <li>
          <h4><span aria-hidden="true">1</span>Where it broke</h4>
          <div className={`fc-verdict tone-${read.tone}${read.network ? " is-network" : ""}`}>
            <div className="fc-verdict-head"><strong>{read.where}</strong><span className="fc-sure">{read.sure}</span></div>
            <p>{read.meaning}</p>
          </div>
          <FlowPath episode={episode} />
        </li>
        <li>
          <h4><span aria-hidden="true">2</span>How we know</h4>
          <ul className="fc-trail">
            {trail(ev, wanName, episode.verdict?.needs_reassessment).map((step) => (
              <li key={step.text} className={`is-${step.tone}`}>
                <i aria-hidden="true">{TRAIL_MARKS[step.tone]}</i>
                <span><strong>{step.text}</strong>{step.detail && <small>{step.detail}</small>}</span>
              </li>
            ))}
          </ul>
        </li>
        <li>
          <h4><span aria-hidden="true">3</span>What to do</h4>
          <p className="fc-next">{read.next}</p>
          <button className="fc-copy" onClick={copy}><FiCopy aria-hidden="true" />{copyState === "copied" ? "Copied" : "Copy report"}</button>
          <span className="fc-copy-hint">Copies the full report, measurements included, ready to paste into an email or ticket.</span>
          <span className="fc-sr-only" role="status">{copyState === "copied" ? "Report copied to clipboard" : ""}</span>
          {copyState === "manual" && (
            <div className="fc-note">
              <p role="status">Clipboard unavailable. Select and copy the report below.</p>
              <textarea aria-label="Report to copy" readOnly value={evidenceText} onFocus={(event) => event.target.select()} />
            </div>
          )}
        </li>
      </ol>
      <details className="fc-raw">
        <summary><FiChevronRight aria-hidden="true" /><span><b className="fc-when-closed">Show</b><b className="fc-when-open">Hide</b> the measurements<em>The traffic chart, the packet counts and the appliance's own wording, for whoever investigates.</em></span></summary>
        <span className="fc-eyebrow">What the box recorded</span>
        <p className="fc-summary">{episode.verdict?.summary || "No summary was recorded for this stall."}</p>
        {ev.incomplete?.length > 0 && <div className="fc-note"><p>Observation gaps: {ev.incomplete.join("; ")}</p></div>}
        <StallChart samples={episode.samples} stalledAt={episode.stalled_at} interval={episode.sample_interval} />
        <div className="fc-evidence-grid">
          <div className="fc-because-box">
            <h4>What the evidence shows</h4>
            {episode.verdict?.because?.length ? <ul className="fc-because">{episode.verdict.because.map((item, i) => <li key={i}>{item}</li>)}</ul>
              : <p>No supporting reasons were recorded.</p>}
          </div>
          {episode.verdict?.missing?.length > 0 && (
            <div className="fc-missing"><FiHelpCircle aria-hidden="true" /><div>
              <strong>What remains unknown</strong>
              <ul>{episode.verdict.missing.map((item, i) => <li key={i}>{item}</li>)}</ul>
            </div></div>
          )}
        </div>
        <div className="fc-facts">
          <Fact label="Episode" mono>{episode.id}</Fact>
          <Fact label="Recorded verdict">{episode.verdict?.segment || "unknown"} · {confidenceLabel(episode.verdict || {})}</Fact>
          <Fact label="Flow" mono>{endpoint(episode.key?.device, episode.key?.device_port)} → {endpoint(episode.key?.dst, episode.key?.dst_port)}</Fact>
          <Fact label="Device">{episode.hostname || episode.key?.device}</Fact>
          <Fact label="Traffic class">{episode.class || "Unclassified"}</Fact>
          <Fact label="Stalled">{when(episode.stalled_at)}</Fact>
          <Fact label="Observation window">{when(ev.observed_from) || "Unknown start"} → {when(ev.observed_until) || "unknown end"} · {ev.observation_complete ? "complete" : "completeness not established"}</Fact>
          <Fact label="Last movement">Sent {clockOnly(episode.last_fwd) || "unknown"} · replies {clockOnly(episode.last_rev) || "unknown"}</Fact>
          <Fact label="Before the stall">Sent {progress(ev.fwd_before)} · replies {progress(ev.rev_before)}</Fact>
          <Fact label="Sent after the stall">{count(ev.fwd_after?.pkts)}</Fact>
          <Fact label="Replies after the stall">{count(ev.rev_after?.pkts)}</Fact>
          <Fact label="New attempts">{count(ev.retries?.flows)}{ev.retries?.unreplied_flows ? ` · ${ev.retries.unreplied_flows} had no reply` : ""}</Fact>
          <Fact label="Other outbound device traffic">{count(ev.device_other_sent_pkts)}{Number.isFinite(ev.device_other_sent_pkts) ? " packets during the observation window" : ""}</Fact>
          <Fact label="Circuit">{ev.circuit?.wan ? <WanLabel iface={ev.circuit.wan} /> : "Not identified"}{ev.circuit?.degraded ? " · degraded" : ""}{ev.circuit?.latency_ms ? ` · ${ev.circuit.latency_ms.toFixed(1)} ms` : ""}</Fact>
          <Fact label="Circuit probe loss">{circuitLoss(ev.circuit)}</Fact>
          <Fact label="Circuit evidence">{ev.circuit?.observed && ev.circuit?.flow_matched ? `Flow placement matched · ${when(ev.circuit.observed_at) || "sample time unknown"}` : "A recent measurement matched to this flow was not established"}{ev.circuit?.note ? ` · ${ev.circuit.note}` : ""}</Fact>
          <Fact label="Matched window">{windowReading(ev.anchor_window)}</Fact>
          <Fact label="Anchor">{anchorReading(ev.anchor)}</Fact>
          <Fact label="Destination probes">{ev.reach?.attempted ? `${count(ev.reach.received)}/${count(ev.reach.sent)} answered${ev.reach.rtt_ms ? ` · ${ev.reach.rtt_ms.toFixed(1)} ms` : ""}` : ev.reach?.note || "Not measured"}</Fact>
          <Fact label="Probe scope">Independent probes do not establish delivery of this flow or application health.</Fact>
          {episode.original_verdict && <Fact label="Original assessment (unvalidated)">{episode.original_verdict.summary || episode.original_verdict.segment}</Fact>}
        </div>
      </details>
    </div>
  );
}

// A live flow's recent movement, from the deltas this panel has watched go by.
function Spark({ history }) {
  if (!history || history.length < 2) return <span className="fc-spark empty" />;
  const peak = Math.max(1, ...history.map((h) => Math.max(h.out, h.back)));
  const W = 64;
  const H = 18;
  const step = W / Math.max(1, history.length - 1);
  const pts = (key) =>
    history.map((h, i) => `${(i * step).toFixed(1)},${(H - (h[key] / peak) * (H - 2)).toFixed(1)}`).join(" ");
  return (
    <svg className="fc-spark" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline points={pts("out")} className="fc-spark-out" fill="none" />
      <polyline points={pts("back")} className="fc-spark-back" fill="none" />
    </svg>
  );
}

export default function FlowContinuityPanel({ nodeApiUrl, selectedNode, trafficClasses = [] }) {
  if (!selectedNode) return <div className="panel-card fc-panel"><div className="fc-empty"><FiSearch aria-hidden="true" /><h2>Select an edge node</h2><p>Choose a box to see its connection interruptions.</p></div></div>;
  return <ContinuityMonitor key={selectedNode} nodeApiUrl={nodeApiUrl} selectedNode={selectedNode} trafficClasses={trafficClasses} />;
}

function ContinuityMonitor({ nodeApiUrl, selectedNode, trafficClasses }) {
  // The edge knows more classes than the console lists, so an unlisted key is tidied rather than shown raw.
  const className = useCallback((key) => trafficClasses.find((c) => c.key === key)?.label
    || (/^[A-Z0-9_]+$/.test(key || "") ? key.replace(/^APP_/, "").toLowerCase().replace(/(^|_)([a-z0-9])/g, (_, gap, letter) => `${gap && " "}${letter.toUpperCase()}`) : key || ""), [trafficClasses]);
  const [snapshot, setSnapshot] = useState(null);
  const [paused, setPaused] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState("");
  const [openEpisode, setOpenEpisode] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  // A phone opens on the list: the timeline is wider than its screen.
  const phone = usePhoneLayout();
  const [historyView, setHistoryView] = useState(phone ? "list" : "timeline");
  const [timeRange, setTimeRange] = useState("all");
  const [showPauses, setShowPauses] = useState(false);
  const panelRef = useRef(null);
  const investigationRef = useRef(null);
  const scrollToInvestigation = useRef(false);
  const [error, setError] = useState("");
  const sparks = useRef(new Map());
  const mounted = useRef(true);
  const pollRequest = useRef(null);
  const detailRequest = useRef(null);
  const { state: featureState } = useEdgeFeature(selectedNode, "/diagnostics/episodes");

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pollRequest.current?.abort();
      detailRequest.current?.abort();
      pollRequest.current = null;
      detailRequest.current = null;
    };
  }, []);

  const getJson = useCallback(async (path, signal) => {
    const url = nodeApiUrl(path);
    if (!url) throw new Error("No node selected");
    const response = await fetchWithTimeout(url, { signal });
    if (!response.ok) throw new Error(response.status === 404 ? "This record is no longer available on the appliance." : `The appliance returned HTTP ${response.status}.`);
    return response.json();
  }, [nodeApiUrl]);

  const poll = useCallback(async () => {
    if (pollRequest.current) return;
    const request = new AbortController();
    pollRequest.current = request;
    setRefreshing(true);
    try {
      const [eps, live] = await Promise.all([
        getJson("/diagnostics/episodes?limit=0", request.signal),
        getJson("/diagnostics/flows", request.signal),
      ]);
      if (!mounted.current || request.signal.aborted) return;
      const rows = Array.isArray(live?.flows) ? live.flows : [];
      const seen = new Set();
      rows.forEach((flow) => {
        const key = flowKey(flow);
        seen.add(key);
        const prev = sparks.current.get(key);
        const now = { out: flow.fwd?.pkts || 0, back: flow.rev?.pkts || 0 };
        const hist = prev && now.out >= prev.last.out && now.back >= prev.last.back ? prev.hist.slice(-23) : [];
        if (prev && now.out >= prev.last.out && now.back >= prev.last.back) hist.push({ out: now.out - prev.last.out, back: now.back - prev.last.back });
        sparks.current.set(key, { last: now, hist });
      });
      [...sparks.current.keys()].forEach((key) => { if (!seen.has(key)) sparks.current.delete(key); });
      setSnapshot({
        episodes: Array.isArray(eps?.episodes) ? eps.episodes.map(assessedSummary) : [],
        flows: rows, stats: live?.stats || {}, watching: Boolean(eps?.available && live?.available !== false), updatedAt: new Date().toISOString(),
      });
      setError("");
    } catch (err) {
      if (mounted.current && !request.signal.aborted) setError(err.message || "The appliance did not respond.");
    } finally {
      if (pollRequest.current === request) {
        pollRequest.current = null;
        if (mounted.current) setRefreshing(false);
      }
    }
  }, [getJson]);

  useVisibleInterval(poll, 5000, !paused && featureState !== FEATURE_ABSENT);

  const togglePaused = () => {
    if (!paused) {
      pollRequest.current?.abort();
      pollRequest.current = null;
      sparks.current.clear();
      setRefreshing(false);
    }
    setPaused(!paused);
  };

  const loadEpisode = async (id) => {
    detailRequest.current?.abort();
    const request = new AbortController();
    detailRequest.current = request;
    setOpenId(id);
    setOpenEpisode(null);
    setDetailError("");
    try {
      const episode = await getJson(`/diagnostics/episodes/${encodeURIComponent(id)}`, request.signal);
      if (!episode?.id || episode.available === false) throw new Error("Episode evidence is unavailable. Flow monitoring may have been disabled.");
      if (episode.id !== id) throw new Error("The appliance returned a different episode. Refresh to try this record again.");
      if (mounted.current && !request.signal.aborted && detailRequest.current === request) setOpenEpisode(assessedEpisode(episode));
    } catch (err) {
      if (mounted.current && !request.signal.aborted && detailRequest.current === request) setDetailError(err.message || "Could not load the episode.");
    }
  };

  const closeEpisode = useCallback((restoreFocus = false) => {
    if (restoreFocus) {
      const marker = [...(panelRef.current?.querySelectorAll("[data-episode-id]") || [])].find((el) => el.dataset.episodeId === openId);
      marker?.focus();
    }
    detailRequest.current?.abort();
    detailRequest.current = null;
    setOpenId("");
    setOpenEpisode(null);
    setDetailError("");
  }, [openId]);

  const open = (id) => {
    if (openId !== id) {
      scrollToInvestigation.current = true;
      loadEpisode(id);
      return;
    }
    closeEpisode(true);
  };

  const episodes = snapshot?.episodes || [];
  const flows = snapshot?.flows || [];
  const stats = snapshot?.stats || {};
  const pauses = episodes.filter(isPause);
  const interruptions = episodes.filter((ep) => !isPause(ep));
  const tally = useMemo(() => {
    const counts = new Map();
    // A brief pause has no place where it broke, so only interruptions are tallied by one.
    (snapshot?.episodes || []).filter((ep) => !isPause(ep)).forEach((ep) => counts.set(ep.segment, (counts.get(ep.segment) || 0) + 1));
    return [...counts.entries()].map(([segment, value]) => ({ segment, count: value, label: placeOf(segment).label })).sort((a, b) => b.count - a.count);
  }, [snapshot]);
  const query = search.trim().toLowerCase();
  const rangeEnd = snapshot ? Date.parse(snapshot.updatedAt) : Date.now();
  const rangeStart = timeRange === "all" ? null : rangeEnd - (timeRange === "day" ? 24 : 7 * 24) * 60 * 60 * 1000;
  const inView = (ep) => (rangeStart === null || !Number.isFinite(recordedTime(ep)) || (recordedTime(ep) >= rangeStart && recordedTime(ep) <= rangeEnd)) && (!query || [ep.class, className(ep.class), ep.hostname, ep.device, ep.dst, ep.headline, reading(ep).label].some((value) => String(value || "").toLowerCase().includes(query)));
  const pool = showPauses ? episodes : interruptions;
  const shown = pool.filter((ep) => inView(ep) && (!filter || (ep.segment === filter && !isPause(ep))));
  // Pauses left off the map still count on their device's row, unless a place is chosen: a pause has none.
  const hiddenPauses = showPauses || filter ? [] : pauses.filter(inView);
  const chronological = [...shown].sort((a, b) => (Number.isFinite(recordedTime(a)) ? recordedTime(a) : Infinity) - (Number.isFinite(recordedTime(b)) ? recordedTime(b) : Infinity) || a.id.localeCompare(b.id));
  const selectedIndex = chronological.findIndex((ep) => ep.id === openId);
  const selectedSummary = chronological[selectedIndex];
  const selectedVisible = selectedIndex !== -1;
  const hasFilters = Boolean(query || filter || timeRange !== "all");
  const clearFilters = () => { setFilter(""); setSearch(""); setTimeRange("all"); };
  const orderedFlows = [...flows].sort((a, b) => Number(Boolean(b.stalling)) - Number(Boolean(a.stalling)));
  const stalling = flows.filter((flow) => flow.stalling).length;
  const networkCount = episodes.filter((ep) => reading(ep).network).length;
  const status = error ? "Updates unavailable" : paused ? "Updates paused" : !snapshot ? "Connecting" : !snapshot.watching ? "Monitoring disabled" : "Live updates";

  useEffect(() => {
    if (openId && !selectedVisible) closeEpisode();
  }, [openId, selectedVisible, closeEpisode]);

  useEffect(() => {
    if (openId && scrollToInvestigation.current) {
      investigationRef.current?.focus({ preventScroll: true });
      investigationRef.current?.scrollIntoView?.({ block: "nearest", behavior: "auto" });
      scrollToInvestigation.current = false;
    }
  }, [openId]);

  if (featureState === FEATURE_ABSENT) return <div className="panel-card fc-panel"><FeatureUnavailableNotice nodeId={selectedNode} feature="Connection interruption tracking" /></div>;

  return (
    <div ref={panelRef} className="panel-card fc-panel">
      <header className="fc-toolbar">
        <div className="fc-statusbar">
          <span className={`fc-monitor-state ${error ? "is-error" : paused || !snapshot?.watching ? "is-idle" : "is-live"}`} role="status"><i />{status}</span>
          <span className="fc-freshness"><FiClock aria-hidden="true" />{snapshot ? `Last updated ${clockOnly(snapshot.updatedAt)}` : "Waiting for this box"}</span>
        </div>
        <div className="fc-toolbar-actions">
          <button className="fc-button" onClick={togglePaused}>{paused ? <FiPlay aria-hidden="true" /> : <FiPause aria-hidden="true" />}{paused ? "Resume updates" : "Pause updates"}</button>
          <button className="fc-button" onClick={poll} disabled={refreshing}><FiRefreshCw aria-hidden="true" className={refreshing ? "fc-spinning" : ""} />Refresh</button>
        </div>
      </header>
      {error && <div className="fc-note error" role="alert"><strong>Could not refresh continuity data.</strong> {error}{snapshot && <span> Showing the last successful snapshot from {clockOnly(snapshot.updatedAt)}.</span>}</div>}
      {!snapshot && <div className="fc-empty" role="status"><FiActivity aria-hidden="true" /><h3>{error ? "Continuity data unavailable" : "Loading continuity data"}</h3><p>{error ? "Use Refresh to try the appliance again." : "Reading recorded stalls and watched flows from this appliance."}</p></div>}
      {snapshot && <>
        {!snapshot.watching && <div className="fc-note"><strong>Flow monitoring is disabled.</strong> Ask the appliance administrator to enable flow monitoring.<details><summary>Administrator setup</summary><p>Set <code>NABX_FLOW_WITNESS_ENABLED=true</code> in the appliance environment.</p></details></div>}
        <div className="fc-headline fc-overview">
          <Stat label="Interruptions recorded" value={interruptions.length} sub={pauses.length ? `plus ${pauses.length} brief ${pauses.length === 1 ? "pause" : "pauses"}` : "kept on this box"} />
          <Stat label="On the network side" value={networkCount} tone={networkCount ? "warn" : ""} sub="an internet line or the Weavian link" />
          <Stat label="Connections being watched" value={snapshot.watching ? stats.watched ?? flows.length : "—"} sub={stats.flows != null ? `of ${stats.flows.toLocaleString()} seen by this box` : "at the last update"} />
          <Stat label="Frozen right now" value={snapshot.watching ? stalling : "—"} tone={snapshot.watching && stalling ? "warn" : ""} sub={stats.pending ? `${stats.pending} being looked into` : "among watched connections"} />
        </div>

        <section className="fc-history" aria-labelledby="fc-history-title">
          <div className="fc-section-heading fc-explorer-heading"><div><span className="fc-eyebrow">History</span><h3 id="fc-history-title">When did they happen? <span>{pool.length}</span></h3><p>{phone ? "Tap" : "Click"} a {historyView === "timeline" ? "dot" : "row"} to see what happened and where it broke. Times are in your local time.</p></div>
            <div className="fc-view-switch" role="group" aria-label="History view">
              <button aria-pressed={historyView === "timeline"} onClick={() => setHistoryView("timeline")}><FiActivity aria-hidden="true" />Timeline</button>
              <button aria-pressed={historyView === "list"} onClick={() => setHistoryView("list")}><FiList aria-hidden="true" />List</button>
            </div>
          </div>
          <div className="fc-explorer-tools">
            <div className="fc-range-switch" role="group" aria-label="Time range">
              {[['all', 'All history'], ['day', '24 hours'], ['week', '7 days']].map(([key, label]) => <button key={key} aria-pressed={timeRange === key} onClick={() => setTimeRange(key)}>{label}</button>)}
            </div>
            {pauses.length > 0 && <label className="fc-pause-switch"><input type="checkbox" checked={showPauses} onChange={(event) => setShowPauses(event.target.checked)} />Show brief pauses<span className="fc-tally-count">{pauses.length}</span></label>}
            <div className="fc-search"><FiSearch aria-hidden="true" /><input type="search" aria-label="Search interruptions" placeholder="Find a device, destination or app" value={search} onChange={(event) => setSearch(event.target.value)} />{search && <button aria-label="Clear search" onClick={() => setSearch("")}><FiX aria-hidden="true" /></button>}</div>
          </div>
          <div className="fc-tally" role="group" aria-label="Filter by where it broke">
            <span className="fc-tally-lead">Where it broke:</span>
            <button className={`fc-tally-item${!filter ? " on" : ""}`} aria-pressed={!filter} onClick={() => setFilter("")}>Anywhere <span className="fc-tally-count">{pool.length}</span></button>
            {tally.map((item) => <button key={item.segment} className={`fc-tally-item${filter === item.segment ? " on" : ""}`} aria-pressed={filter === item.segment} onClick={() => setFilter(filter === item.segment ? "" : item.segment)}>{item.label}<span className="fc-tally-count">{item.count}</span></button>)}
          </div>
          {hasFilters && <div className="fc-results" role="status">{shown.length} of {pool.length} interruptions match<button className="fc-text-button" onClick={clearFilters}>Clear filters</button></div>}
          {timeRange !== "all" && shown.some((ep) => !Number.isFinite(recordedTime(ep))) && <p className="fc-list-caption">Records without a timestamp remain below the map.</p>}
          {shown.length === 0 && (historyView === "list" || hiddenPauses.length === 0) ? <div className="fc-empty fc-history-empty"><FiSearch aria-hidden="true" />{hiddenPauses.length ? <><h4>Only brief pauses here</h4><p>Connections that stopped for a few seconds and recovered on their own. They stay hidden unless you ask for them.</p><button className="fc-button" onClick={() => setShowPauses(true)}>Show brief pauses</button></>
            : <><h4>{hasFilters ? "Nothing matches" : "No interruptions recorded"}</h4><p>{hasFilters ? "Try a wider time range or clear the filters." : snapshot.watching ? "Nothing this box watches has frozen. A new record appears here when a busy connection stops." : "Records will appear after flow monitoring is enabled and a connection freezes."}</p>{hasFilters && <button className="fc-button" onClick={clearFilters}>Show all history</button>}</>}</div>
            : historyView === "timeline" ? <ContinuityTimeline episodes={shown} hiddenPauses={hiddenPauses} selectedId={openId} onSelect={open} onClearSelection={() => closeEpisode(true)} startAt={rangeStart} endAt={rangeStart === null ? undefined : rangeEnd} detailId="fc-selected-episode" classLabel={className} /> : <div className="fc-list">
            <p className="fc-list-caption">Newest first</p>
            {shown.map((ep) => {
              const read = reading(ep);
              const isOpen = openId === ep.id;
              return <article key={ep.id} className={`fc-row${isOpen ? " open" : ""}`}>
                <button data-episode-id={ep.id} className="fc-row-head" onClick={() => open(ep.id)} aria-expanded={isOpen} aria-controls="fc-selected-episode">
                  <PathStrip segment={ep.segment} confidence={ep.confidence} />
                  <span className="fc-what"><strong>{headline({ className: className(ep.class), device: ep.hostname || ep.device, recovered: ep.recovered })}</strong><span className="fc-kind">{KINDS[ep.kind] || "Traffic stopped."}</span></span>
                  <span className="fc-row-verdict"><span className={`fc-seg tone-${read.tone}${read.network ? " is-network" : ""}`}>{read.label}</span><span className="fc-confidence">{read.sure}</span></span>
                  <span className="fc-when"><time dateTime={ep.stalled_at}>{Number.isFinite(recordedTime(ep)) ? when(ep.stalled_at) : "Time unavailable"}</time><em>{Number.isFinite(recordedTime(ep)) ? ago(ep.stalled_at) : ""}</em></span>
                  <span className="fc-chev">{isOpen ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}</span>
                </button>
              </article>;
            })}
          </div>}
          {episodes.some((ep) => ep.needs_reassessment) && <details className="fc-assessment-note"><summary><FiHelpCircle aria-hidden="true" /><strong>Some older records have no location.</strong></summary><p>They were made with an older method that could point at the wrong place. Their measurements are kept, and their location is shown as not known.</p></details>}
          {selectedVisible && <section ref={investigationRef} id="fc-selected-episode" className="fc-investigation" tabIndex={-1} aria-labelledby="fc-selected-title" onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); closeEpisode(true); } }}>
            <div className="fc-investigation-nav">
              <div><span className="fc-eyebrow">What happened · interruption {selectedIndex + 1} of {shown.length}</span><h4 id="fc-selected-title">{headline({ className: className(selectedSummary.class), device: selectedSummary.hostname || selectedSummary.device, recovered: selectedSummary.recovered })}</h4><p>{Number.isFinite(recordedTime(selectedSummary)) ? when(selectedSummary.stalled_at) : "Time unavailable"} · {KINDS[selectedSummary.kind] || "Traffic stopped."}</p></div>
              <div className="fc-event-navigation">
                <button aria-label="Previous episode" disabled={selectedIndex === 0} onClick={() => loadEpisode(chronological[selectedIndex - 1].id)}><FiChevronLeft aria-hidden="true" />Earlier</button>
                <button aria-label="Next episode" disabled={selectedIndex === chronological.length - 1} onClick={() => loadEpisode(chronological[selectedIndex + 1].id)}>Later<FiChevronRight aria-hidden="true" /></button>
                <button aria-label="Close episode" onClick={() => closeEpisode(true)}><FiX aria-hidden="true" />Close</button>
              </div>
            </div>
            {detailError ? <div className="fc-note error" role="alert">Could not load this episode. {detailError}<button className="fc-button" onClick={() => loadEpisode(openId)}>Retry episode</button></div>
              : openEpisode ? <EpisodeDetail key={openId} episode={openEpisode} /> : <div className="fc-detail-loading" role="status">Reading the episode…</div>}
          </section>}
        </section>

        {snapshot.watching && <details className="fc-live-disclosure" open={episodes.length === 0 || undefined}>
          <summary><span><FiActivity aria-hidden="true" />Connections being watched right now <em>{flows.length} watched{stalling ? ` · ${stalling} frozen` : ""}</em></span><FiChevronDown aria-hidden="true" /></summary>
          <section className="fc-live" aria-labelledby="fc-live-title">
          <div className="fc-section-heading"><div><h3 id="fc-live-title"><FiActivity aria-hidden="true" />Connections being watched</h3><p>Frozen connections appear first. Packet counts are totals for each connection.</p></div>
            {flows.length > 0 && (stalling ? <span className="fc-live-alert"><FiAlertTriangle aria-hidden="true" />{stalling} stalling</span> : <span className="fc-meta"><FiHelpCircle aria-hidden="true" /> No stalls reported in this snapshot</span>)}
          </div>
          <p className="fc-meta">{typeof stats.last_observed_at === "string" && !stats.last_observed_at.startsWith("0001-") && when(stats.last_observed_at) ? `Last traffic sample ${when(stats.last_observed_at)}. ` : "Traffic sample freshness is unavailable on this appliance. "}Coverage is limited to eligible, previously active flows; an empty or quiet view does not establish connection health.</p>
          {flows.length === 0 ? <div className="fc-live-empty"><strong>Waiting for eligible flows</strong><p>Flows appear once they carry enough traffic to be watched. An empty list does not establish connection health.</p></div> : <>
            <div className="fc-table-scroll" role="region" aria-label="Watched flows" tabIndex={0}><table className="fc-live-table"><thead><tr>{FLOW_COLUMNS.map((c, i) => <th key={c} scope="col" className={i > 4 ? "num" : undefined}>{c}</th>)}</tr></thead><tbody>
              {(showAll ? orderedFlows : orderedFlows.slice(0, 8)).map((flow) => <tr key={flowKey(flow)} className={flow.stalling ? "stalling" : ""}><td data-label={FLOW_COLUMNS[0]}><span className={`fc-flow-state${flow.stalling ? " is-stalling" : ""}`}>{flow.stalling ? "Stalling" : "Watching"}</span></td><td data-label={FLOW_COLUMNS[1]}>{flow.class || "Unclassified"}</td><td data-label={FLOW_COLUMNS[2]} className="mono">{endpoint(flow.key?.device, flow.key?.device_port)}</td><td data-label={FLOW_COLUMNS[3]} className="mono">{endpoint(flow.key?.dst, flow.key?.dst_port)}</td><td data-label={FLOW_COLUMNS[4]}><Spark history={sparks.current.get(flowKey(flow))?.hist} /></td><td data-label={FLOW_COLUMNS[5]} className="num">{count(flow.fwd?.pkts)}</td><td data-label={FLOW_COLUMNS[6]} className="num">{count(flow.rev?.pkts)}</td></tr>)}
            </tbody></table></div>
            <div className="fc-table-footer"><span>Showing {showAll ? flows.length : Math.min(8, flows.length)} of {flows.length} watched flows</span>{flows.length > 8 && <button className="fc-text-button" onClick={() => setShowAll(!showAll)}>{showAll ? "Show fewer flows" : `Show all ${flows.length} flows`}</button>}<span className="fc-spark-legend"><i />Sent <i />Replies · movement between updates</span></div>
          </>}
        </section></details>}
        {stats.dropped_over_capacity > 0 && <div className="fc-note">Monitoring capacity was exceeded: {stats.dropped_over_capacity.toLocaleString()} flows were not admitted. This view does not cover all traffic.</div>}
      </>}
      <p className="fc-footnote">Device names and destinations stay on this box. Only the conclusion is reported to the fleet.</p>
    </div>
  );
}
